import React, { useEffect, useMemo, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { formatDate, todayISO } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import ExportPreview from "@/components/ExportPreview";
import { F } from "@/pages/Employees";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, LogIn, AlertTriangle, Clock, Eye } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };

export default function ReportAttendanceRange() {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("all");
  const [start, setStart] = useState(firstOfMonth());
  const [end, setEnd] = useState(todayISO());
  const [rows, setRows] = useState([]);
  const [ringkasan, setRingkasan] = useState({ total: 0, hadir: 0, telat: 0, total_lembur: 0 });
  const [preview, setPreview] = useState(false);
  const [mode, setMode] = useState("karyawan");
  const [dups, setDups] = useState([]);
  const [empId, setEmpId] = useState("all");
  useEffect(() => {
    if (!start || !end) return;
    api.get(`/attendance/duplicates?start=${start}&end=${end}`).then((r) => setDups(r.data || [])).catch(() => setDups([]));
  }, [start, end, rows]);
  const perEmp = useMemo(() => aggEmp(rows), [rows]);
  const perProj = useMemo(() => {
    const g = {};
    rows.forEach((r) => { (g[r.project_nama || "-"] = g[r.project_nama || "-"] || []).push(r); });
    return Object.keys(g).sort().map((k) => ({ project_nama: k, employees: aggEmp(g[k]) }));
  }, [rows]);

  useEffect(() => { api.get("/projects").then((r) => setProjects(r.data)).catch(() => {}); }, []);

  const load = async () => {
    if (!start || !end) return;
    try {
      const q = `start=${start}&end=${end}${projectId && projectId !== "all" ? `&project_id=${projectId}` : ""}`;
      const r = await api.get(`/reports/attendance-range?${q}`);
      setRows(r.data.rows);
      setRingkasan(r.data.ringkasan);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [start, end, projectId]);

  const dlUrl = (fmt) => {
    const query = `start=${start}&end=${end}${projectId && projectId !== "all" ? `&project_id=${projectId}` : ""}`;
    const emp = mode === "pegawai" && empId !== "all" ? `&employee_id=${empId}` : "";
    return { url: `/reports/attendance-range/${fmt}?${query}${emp}${fmt === "pdf" ? `&group=${mode}` : ""}`, filename: `rekap_absensi_${mode}_${start}_${end}.${fmt}` };
  };

  const columns = [
    { key: "tanggal", label: "Tanggal", sortable: true, render: (r) => formatDate(r.tanggal) },
    { key: "employee_nama", label: "Pegawai", sortable: true, render: (r) => <div><span className="font-semibold text-slate-800">{r.employee_nama}</span><div className="text-xs text-slate-400">{r.kode_pegawai}</div></div> },
    { key: "project_nama", label: "Proyek", sortable: true },
    { key: "status", label: "Status", render: (r) => <span className="text-xs font-semibold">{r.status}</span> },
    { key: "jam_masuk", label: "Jam Datang", render: (r) => r.jam_masuk || <span className="text-slate-300">—</span> },
    { key: "jam_pulang", label: "Jam Pulang", render: (r) => r.jam_pulang || <span className="text-slate-300">—</span> },
    { key: "telat", label: "Telat", sortable: true, sortValue: (r) => r.menit_telat, render: (r) => r.telat ? <span className="rounded bg-rose-100 px-2 py-0.5 text-xs font-bold text-rose-700">TELAT {r.menit_telat}m</span> : <span className="text-emerald-600 text-xs font-semibold">Tepat</span> },
    { key: "jam_lembur", label: "Lembur (jam)", align: "right", render: (r) => r.jam_lembur || 0 },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Rekap Absensi" subtitle="Per karyawan (hari masuk & jam lembur), per proyek, atau detail harian — pratinjau lalu unduh PDF / Excel">
        {rows.length > 0 && (
          <Button variant="outline" onClick={() => setPreview(true)} data-testid="rr-preview"><Eye className="h-4 w-4 mr-1" /> Pratinjau & Unduh</Button>
        )}
      </PageHeader>
      <Card className="p-4 no-print">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
          <F label="Tanggal Mulai"><Input type="date" value={start} max={end || undefined} data-testid="rr-start" onChange={(e) => setStart(e.target.value)} /></F>
          <F label="Tanggal Akhir"><Input type="date" value={end} min={start || undefined} data-testid="rr-end" onChange={(e) => setEnd(e.target.value)} /></F>
          <F label="Proyek">
            <Select value={projectId} onValueChange={setProjectId}>
              <SelectTrigger data-testid="rr-project"><SelectValue placeholder="Semua proyek" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Proyek</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
        </div>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Users} label="Total Record" value={ringkasan.total} tone="slate" />
        <Stat icon={LogIn} label="Hadir" value={ringkasan.hadir} tone="emerald" />
        <Stat icon={AlertTriangle} label="Telat" value={ringkasan.telat} tone="rose" />
        <Stat icon={Clock} label="Total Lembur (jam)" value={Math.round((ringkasan.total_lembur || 0) * 10) / 10} tone="indigo" />
      </div>

      {dups.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-rose-300 bg-rose-50 px-4 py-3 text-sm text-rose-800" data-testid="rr-dup-warning">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <span className="flex-1"><b>Ada {dups.length} absensi dobel di rentang ini</b> ({[...new Set(dups.map((d) => d.employee_nama))].slice(0, 6).join(", ")}{new Set(dups.map((d) => d.employee_nama)).size > 6 ? ", dll" : ""}) — hari masuk jadi lebih dari hari kerja. Rapikan dulu agar rekap & gaji benar.</span>
          <Link to="/attendance?dobel=1" className="rounded-lg bg-rose-700 px-3 py-2 font-semibold text-white hover:bg-rose-800" data-testid="rr-dup-fix-link">Rapikan Sekarang</Link>
        </div>
      )}

      <div className="flex flex-wrap gap-2" data-testid="rr-mode">
        {[["karyawan", "Per Karyawan"], ["pegawai", "Detail per Karyawan"], ["proyek", "Per Proyek"], ["detail", "Detail Harian"]].map(([k, l]) => (
          <Button key={k} size="sm" variant={mode === k ? "default" : "outline"} className={mode === k ? "bg-blue-800 hover:bg-blue-900" : ""}
            data-testid={`rr-mode-${k}`} onClick={() => setMode(k)}>{l}</Button>
        ))}
        <span className="ml-auto self-center text-xs text-slate-400">Data diperbarui otomatis tiap 1 menit</span>
      </div>

      {mode === "karyawan" && (
        <DataTable columns={empColumns} data={perEmp} searchKeys={["employee_nama", "kode_pegawai", "proyek"]} pageSize={20} testid="rr-emp-table"
          toolbar={<span className="text-sm text-slate-500">{perEmp.length} pegawai</span>} />
      )}
      {mode === "pegawai" && <EmpDaily rows={rows} emps={perEmp} empId={empId} setEmpId={setEmpId} />}
      {mode === "proyek" && <ProjectGroups groups={perProj} />}
      {mode === "detail" && (
      <DataTable columns={columns} data={rows} searchKeys={["employee_nama", "project_nama", "kode_pegawai", "tanggal"]} pageSize={15} testid="rr-table"
        toolbar={<span className="text-sm text-slate-500">{rows.length} record</span>} />
      )}

      <ExportPreview open={preview} onClose={() => setPreview(false)} title={`Pratinjau Rekap Absensi ${start} s/d ${end}`}
        pdfUrl={dlUrl("pdf").url}
        items={[
          { label: "Unduh PDF", ...dlUrl("pdf") },
          { label: "Unduh Excel", ...dlUrl("xlsx") },
        ]} />
    </div>
  );
}

const hari = (r) => (r.status === "Hadir" ? 1 : r.status === "Setengah Hari" ? 0.5 : 0);
const aggEmp = (rows) => {
  const m = {};
  rows.forEach((r) => {
    const k = r.employee_id || r.kode_pegawai || r.employee_nama;
    const a = (m[k] = m[k] || { key: k, employee_nama: r.employee_nama, kode_pegawai: r.kode_pegawai, proyekSet: new Set(), hari_masuk: 0, tidak_masuk: 0, telat: 0, jam_lembur: 0 });
    a.proyekSet.add(r.project_nama || "-");
    const h = hari(r);
    a.hari_masuk += h; a.tidak_masuk += h ? 0 : 1; a.telat += r.telat ? 1 : 0; a.jam_lembur += Number(r.jam_lembur || 0);
  });
  return Object.values(m).map((a) => ({ ...a, proyek: [...a.proyekSet].sort().join(", "), jam_lembur: Math.round(a.jam_lembur * 10) / 10 }))
    .sort((x, y) => (x.employee_nama || "").localeCompare(y.employee_nama || ""));
};
const sum = (arr, k) => Math.round(arr.reduce((s, a) => s + a[k], 0) * 10) / 10;

const empColumns = [
  { key: "employee_nama", label: "Pegawai", sortable: true, render: (r) => <div><span className="font-semibold text-slate-800">{r.employee_nama}</span><div className="text-xs text-slate-400">{r.kode_pegawai}</div></div> },
  { key: "proyek", label: "Proyek", sortable: true },
  { key: "hari_masuk", label: "Hari Masuk", align: "right", sortable: true, render: (r) => <span className="font-mono font-bold text-emerald-700">{r.hari_masuk}</span> },
  { key: "tidak_masuk", label: "Tidak Masuk", align: "right", sortable: true },
  { key: "telat", label: "Telat (x)", align: "right", sortable: true },
  { key: "jam_lembur", label: "Lembur (jam)", align: "right", sortable: true, render: (r) => <span className="font-mono font-bold text-indigo-700">{r.jam_lembur}</span> },
];

const ProjectGroups = ({ groups }) => (
  <div className="space-y-4" data-testid="rr-proj-groups">
    {groups.length === 0 && <Card className="p-8 text-center text-slate-400">Tidak ada data absensi.</Card>}
    {groups.map((g, gi) => (
      <div key={g.project_nama} className="overflow-x-auto scroll-thin rounded-xl border border-slate-200 bg-white" data-testid={`rr-proj-${gi}`}>
        <div className="bg-orange-500 px-4 py-2 font-heading font-bold text-white">PROYEK: {g.project_nama}</div>
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-slate-100 text-xs font-bold uppercase tracking-wider text-slate-500">
            <th className="px-3 py-2 text-left w-10">No</th><th className="px-3 py-2 text-left">Pegawai</th>
            <th className="px-3 py-2 text-right">Hari Masuk</th><th className="px-3 py-2 text-right">Telat (x)</th><th className="px-3 py-2 text-right">Lembur (jam)</th>
          </tr></thead>
          <tbody>{g.employees.map((a, i) => (
            <tr key={a.key} className="border-b border-slate-100">
              <td className="px-3 py-2 text-slate-400">{i + 1}</td><td className="px-3 py-2 font-semibold text-slate-800">{a.employee_nama}</td>
              <td className="px-3 py-2 text-right font-mono">{a.hari_masuk}</td><td className="px-3 py-2 text-right font-mono">{a.telat}</td>
              <td className="px-3 py-2 text-right font-mono">{a.jam_lembur}</td>
            </tr>
          ))}</tbody>
          <tfoot><tr className="bg-orange-50 font-bold text-slate-800" data-testid={`rr-proj-subtotal-${gi}`}>
            <td className="px-3 py-2" /><td className="px-3 py-2">SUBTOTAL ({g.employees.length} pegawai)</td>
            <td className="px-3 py-2 text-right font-mono">{sum(g.employees, "hari_masuk")}</td>
            <td className="px-3 py-2 text-right font-mono">{sum(g.employees, "telat")}</td>
            <td className="px-3 py-2 text-right font-mono">{sum(g.employees, "jam_lembur")}</td>
          </tr></tfoot>
        </table>
      </div>
    ))}
  </div>
);

const EmpDaily = ({ rows, emps, empId, setEmpId }) => {
  const groups = useMemo(() => {
    const g = {};
    rows.forEach((r) => {
      const k = r.employee_id || r.employee_nama;
      if (empId !== "all" && k !== empId) return;
      (g[k] = g[k] || { key: k, employee_nama: r.employee_nama, kode_pegawai: r.kode_pegawai, rows: [] }).rows.push(r);
    });
    return Object.values(g).sort((a, b) => (a.employee_nama || "").localeCompare(b.employee_nama || ""))
      .map((x) => ({ ...x, rows: [...x.rows].sort((a, b) => a.tanggal.localeCompare(b.tanggal)) }));
  }, [rows, empId]);
  return (
    <div className="space-y-4" data-testid="rr-emp-daily">
      <div className="max-w-sm">
        <Select value={empId} onValueChange={setEmpId}>
          <SelectTrigger data-testid="rr-emp-select"><SelectValue placeholder="Pilih karyawan" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Karyawan</SelectItem>
            {emps.map((e) => <SelectItem key={e.key} value={e.key}>{e.employee_nama}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {groups.length === 0 && <Card className="p-8 text-center text-slate-400">Tidak ada data absensi.</Card>}
      {groups.map((g, gi) => <EmpDailyTable key={g.key} g={g} gi={gi} />)}
    </div>
  );
};

const EmpDailyTable = ({ g, gi }) => {
  const tot = Math.round(g.rows.reduce((s, r) => s + hari(r), 0) * 10) / 10;
  const lem = Math.round(g.rows.reduce((s, r) => s + Number(r.jam_lembur || 0), 0) * 10) / 10;
  return (
    <div className="overflow-x-auto scroll-thin rounded-xl border border-slate-200 bg-white" data-testid={`rr-emp-daily-${gi}`}>
      <div className="bg-orange-500 px-4 py-2 font-heading font-bold text-white">{g.employee_nama} <span className="text-xs font-normal opacity-80">{g.kode_pegawai}</span></div>
      <table className="w-full text-sm">
        <thead><tr className="border-b bg-blue-900 text-xs font-bold uppercase tracking-wider text-white">
          <th className="px-3 py-2 text-left">Tanggal</th><th className="hidden sm:table-cell px-3 py-2 text-left">Nama</th><th className="px-3 py-2 text-left">Lokasi Proyek</th>
          <th className="px-3 py-2 text-center">Hari Masuk</th><th className="px-3 py-2 text-center">Lembur</th>
        </tr></thead>
        <tbody>{g.rows.map((r, i) => (
          <tr key={i} className="border-b border-slate-100">
            <td className="px-3 py-2 font-mono whitespace-nowrap">{formatDate(r.tanggal)}</td><td className="hidden sm:table-cell px-3 py-2">{r.employee_nama}</td>
            <td className="px-3 py-2">{r.project_nama}</td><td className="px-3 py-2 text-center font-mono">{hari(r)}</td>
            <td className="px-3 py-2 text-center font-mono">{r.jam_lembur || 0}</td>
          </tr>
        ))}</tbody>
        <tfoot><tr className="bg-orange-50 font-bold text-slate-800" data-testid={`rr-emp-daily-total-${gi}`}>
          <td className="hidden sm:table-cell" /><td className="px-3 py-2" colSpan={2}>TOTAL ({g.rows.length} hari tercatat)</td>
          <td className="px-3 py-2 text-center font-mono">{tot}</td><td className="px-3 py-2 text-center font-mono">{lem}</td>
        </tr></tfoot>
      </table>
    </div>
  );
};

const TONE = {
  slate: "bg-slate-100 text-slate-700", emerald: "bg-emerald-100 text-emerald-700",
  rose: "bg-rose-100 text-rose-700", indigo: "bg-indigo-100 text-indigo-700",
};
const Stat = ({ icon: Icon, label, value, tone }) => (
  <Card className="p-4 flex items-center gap-3" data-testid={`rr-stat-${label}`}>
    <span className={`flex h-10 w-10 items-center justify-center rounded-lg ${TONE[tone]}`}><Icon className="h-5 w-5" /></span>
    <div>
      <div className="text-2xl font-black text-slate-800">{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  </Card>
);
